/**
 * Zod validation schemas for sale cancellation operations
 * Aligned with API types from @kisanlink/erp-service
 */
import { z } from 'zod';
import type {
  CancelSaleRequest,
  SaleCancellationResponse,
  SaleCancellationItemResponse,
} from '@kisanlink/erp-service';

/**
 * Predefined cancellation reason categories for analytics
 */
export const CANCELLATION_REASONS = [
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'payment_failed', label: 'Payment Failed' },
  { value: 'out_of_stock', label: 'Out of Stock' },
  { value: 'pricing_error', label: 'Pricing Error' },
  { value: 'duplicate_order', label: 'Duplicate Order' },
  { value: 'fraud_suspected', label: 'Fraud Suspected' },
  { value: 'system_error', label: 'System Error' },
  { value: 'other', label: 'Other' },
] as const;

export type CancellationReasonValue = (typeof CANCELLATION_REASONS)[number]['value'];

/**
 * Schema for cancelling an entire sale
 * Matches CancelSaleRequest from @kisanlink/erp-service
 */
export const cancelSaleRequestSchema = z.object({
  reason: z.string().optional(),
  cancelled_by: z.string().optional(),
}) satisfies z.ZodType<CancelSaleRequest>;

/**
 * Schema for cancelling a specific sale item
 * Uses sale_item_id format as expected by the backend API
 */
export const cancelSaleItemSchema = z.object({
  sale_item_id: z.string().min(1, 'Sale Item ID is required'),
  quantity: z.number().positive('Quantity must be positive').optional(),
  reason: z.string().optional(),
});

/**
 * Schema for cancelling multiple items from a sale
 * Uses lowercase/snake_case format as expected by the backend API
 */
export const cancelSaleItemsRequestSchema = z.object({
  reason: z.string().min(1, 'Reason is required'),
  reason_details: z.string().optional(),
  performed_by: z.string().min(1, 'Performed by is required'),
  items: z.array(cancelSaleItemSchema).min(1, 'At least one item must be selected'),
});

/**
 * Schema for individual cancelled item response
 * Matches SaleCancellationItemResponse from @kisanlink/erp-service
 */
export const saleCancellationItemResponseSchema = z.object({
  id: z.string(),
  cancellation_id: z.string(),
  sale_item_id: z.string(),
  batch_id: z.string(),
  quantity_cancelled: z.number().nonnegative(),
  refund_amount: z.number().nonnegative(),
  inventory_transaction_id: z.string().optional(),
  created_at: z.string(),
}) satisfies z.ZodType<SaleCancellationItemResponse>;

/**
 * Schema for sale cancellation response
 * Matches SaleCancellationResponse from @kisanlink/erp-service
 */
export const saleCancellationResponseSchema = z.object({
  id: z.string(),
  sale_id: z.string(),
  cancellation_type: z.enum(['full', 'partial']),
  reason: z.string().optional(),
  cancelled_by: z.string(),
  cancelled_at: z.string(),
  items: z.array(saleCancellationItemResponseSchema),
  inventory_returned: z.boolean(),
  discount_reversed: z.boolean(),
  tax_voided: z.boolean(),
  refund_amount: z.number().nonnegative(),
  created_at: z.string(),
}) satisfies z.ZodType<SaleCancellationResponse>;

/**
 * Form schema for the CancelOrderModal component
 * Includes validation for required reason category
 */
export const cancelOrderFormSchema = z.object({
  reasonCategory: z.string().min(1, 'Please select a cancellation reason'),
  additionalNotes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
});

/**
 * Form schema for the PartialCancelModal component
 * Validates item selection with quantity
 */
export const partialCancelFormSchema = z.object({
  items: z
    .array(
      z.object({
        item_id: z.string(),
        quantity: z.number().positive('Quantity must be at least 1'),
        maxQuantity: z.number(), // For validation reference
        reason: z.string().optional(),
      })
    )
    .min(1, 'Select at least one item to cancel'),
  overallReason: z.string().optional(),
});

// Type exports for component usage
export type CancelSaleFormData = z.infer<typeof cancelOrderFormSchema>;
export type PartialCancelFormData = z.infer<typeof partialCancelFormSchema>;
export type SaleCancellationItem = z.infer<typeof saleCancellationItemResponseSchema>;
export type SaleCancellation = z.infer<typeof saleCancellationResponseSchema>;
